/** Edición de requerimiento: nombre + fecha requerida + descripción (vuelve al detalle). */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput, MobileTextarea } from '@/components/MobileInput';
import { SectionCard } from '@/components/SectionCard';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useMobileRequirementQuery,
  useUpdateRequirementMutation,
} from '@/hooks/useMobileRequirements';
import {
  mobileRequirementEditSchema,
  type MobileRequirementEditForm,
} from '@/schemas/requirement.schemas';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { hapticSuccess } from '@/utils/haptic';

function todayIso(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function RequirementEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requirementId = id ?? '';
  const requirementQuery = useMobileRequirementQuery(requirementId || null);
  const updateMutation = useUpdateRequirementMutation(requirementId);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const requirement = requirementQuery.data;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MobileRequirementEditForm>({
    resolver: yupResolver(mobileRequirementEditSchema),
    values: requirement
      ? {
          name: requirement.name ?? '',
          required_date: requirement.required_date ?? '',
          description: requirement.description ?? '',
        }
      : undefined,
    defaultValues: { name: '', required_date: '', description: '' },
  });

  async function onSubmit(values: MobileRequirementEditForm) {
    if (!requirement) return;
    setGeneralError(null);
    try {
      await updateMutation.mutateAsync({
        body: {
          name: values.name.trim(),
          required_date: values.required_date,
          description: values.description?.trim() || null,
        },
        revision: requirement.workflow_revision,
      });
      hapticSuccess();
      toast.success('Requerimiento actualizado correctamente');
      navigate(`/m/requirements/${requirementId}`, { replace: true });
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    }
  }

  if (requirementQuery.isPending) {
    return (
      <MobileLayout title="Editar requerimiento" showBack>
        <CardListSkeleton count={2} />
      </MobileLayout>
    );
  }

  if (requirementQuery.isError || !requirement) {
    return (
      <MobileLayout title="Editar requerimiento" showBack>
        <EmptyState
          icon="error"
          title="No se pudo cargar"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void requirementQuery.refetch()}
        />
      </MobileLayout>
    );
  }

  const busy = isSubmitting || updateMutation.isPending;

  return (
    <MobileLayout title="Editar requerimiento" subtitle={requirement.code} showBack>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <SectionCard title="Datos del requerimiento">
          <div className="space-y-4">
            <MobileInput
              label="Nombre"
              placeholder="Ej: Ampliacion de planta norte"
              staticLabel
              isRequired
              icon={<MobileIcon name="title" className="h-5 w-5" />}
              error={errors.name?.message}
              {...register('name')}
            />
            <MobileInput
              label="Fecha requerida"
              type="date"
              staticLabel
              isRequired
              min={todayIso()}
              icon={<MobileIcon name="schedule" className="h-5 w-5" />}
              error={errors.required_date?.message}
              {...register('required_date')}
            />
            <MobileTextarea
              label="Descripcion"
              rows={3}
              placeholder="Detalle breve del pedido..."
              {...register('description')}
            />
            {errors.description && (
              <p className="break-words text-sm text-error">{errors.description.message}</p>
            )}
          </div>
        </SectionCard>

        {generalError && (
          <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
          </div>
        )}

        <MobileButton
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          icon={<MobileIcon name="save" className="h-5 w-5" />}
        >
          Guardar cambios
        </MobileButton>
      </form>
    </MobileLayout>
  );
}
